import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { routes } from 'data/routes'
import { shortArticleDate } from 'utils'

import { IArticleHeadProps } from '../types'

const ArticleHead = (props: IArticleHeadProps) => {
  const { t } = useTranslation('community')
  const { locale: languageCode } = useRouter()
  const postDate = new Date(props.communityPost.first_publication_date)
  const diffInDays = shortArticleDate(postDate, languageCode ?? 'en')
  const charCount = (props.communityPost.data.slices as any)?.reduce(
    (acc: number, slice: any) =>
      acc + JSON.stringify(slice?.primary ?? '').length,
    0
  )

  const categoryLink = useMemo(
    () =>
      routes[`/community/category/${props.communityPost.data.category}`]?.[
        languageCode
      ] ?? '',
    [languageCode, props.communityPost.data.category]
  )

  return (
    <div>
      <div>
        <h2 className="text-center text-lg font-semibold uppercase tracking-wide text-red-550">
          {props.communityPost.data.type}
        </h2>
        <h1 className="my-16 text-center text-5xl font-bold">
          {props.communityPost.data.title}
          <i className="dot" />
        </h1>
      </div>
      <div className="flex flex-col items-center justify-between gap-5 md:flex-row">
        <div className="flex items-center gap-3">
          {props.communityPost.data.AuthorImage.url && (
            <div className="relative h-16 w-16 overflow-hidden rounded-full">
              <Image
                src={props.communityPost.data.AuthorImage.url ?? ''}
                alt={props.communityPost.data.AuthorImage.alt ?? ''}
                style={{ objectFit: 'cover', objectPosition: 'top' }}
                fill
              />
            </div>
          )}
          {props.communityPost.data.AuthorName && (
            <div>
              <div className="text-medium text-lg font-semibold leading-none">
                {props.communityPost.data.AuthorName}
              </div>
              <div className="font-medium text-gray-500">
                {props.communityPost.data.AuthorTitle}
              </div>
            </div>
          )}
        </div>
        <div className="text-sm text-gray-500">
          <span>{diffInDays}</span>
          <span className="mx-1">{' | '}</span>
          <Link href={categoryLink}>
            <span className="underline hover:text-red-500">
              {t(`category.${props.communityPost.data.category}`)}
            </span>
          </Link>
          <span className="mx-1">{' | '}</span>
          <span>
            {t('community:readTime', { value: Math.ceil(charCount / 400) })}
          </span>
        </div>
      </div>
      <div className="relative mt-10 h-80">
        <Image
          src={props.communityPost.data.featuredImage.url ?? ''}
          alt={props.communityPost.data.featuredImage.alt ?? ''}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          fill
        />
      </div>
    </div>
  )
}
export default ArticleHead
